import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Button,
    Modal,
    Form,
    Row,
    Col,
    OverlayTrigger,
    Tooltip,
    Badge,
} from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import userApi from "../../services/userapi";
import { showConfirm } from "react-confirm-prompt";
import UserCreateForm from "./UserCreateForm";
import LogModal from "./LogModel";
import branchApi from "../../services/branchapi";
import employeeApi from "../../services/employeeapi";
import menuApi from "../../services/menuapi";
import securityApi from "../../services/securityapi";
import AssignDeliveryBranchesModal from "./AssignDeliveryBranchesModal";

const AllUsers = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [refreshTable, setRefreshTable] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);

    const [openInactivateModal, setOpenInactivateModal] = useState(false);
    const [deactivationReason, setDeactivationReason] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserStatus, setSelectedUserStatus] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [userLogs, setUserLogs] = useState([]);
    const [showLogModal, setShowLogModal] = useState(false);
    const [statusTypes, setStatusTypes] = useState({
        1: "Active",
        2: "Inactive",
        3: "Blocked",
        9: "Deleted",
    });

    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedUserType, setSelectedUserType] = useState("");
    const [selectedRoleFilter, setSelectedRoleFilter] = useState("");
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");

    // Role modal
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [roles, setRoles] = useState([]);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [roleAssigningUserId, setRoleAssigningUserId] = useState(null);
    const [roleLoading, setRoleLoading] = useState(false);

    // Change password modal
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordUserId, setPasswordUserId] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [passwordChanging, setPasswordChanging] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    // Revenue cards password modal (Super Admin only)
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [showDashPwdModal, setShowDashPwdModal] = useState(false);
    const [dashUser, setDashUser] = useState(null); // {id,name,username,dashboardCardsEnabled,dashboardCardsHasPassword}
    const [dashPwd1, setDashPwd1] = useState("");
    const [dashPwd2, setDashPwd2] = useState("");
    const [dashPwdError, setDashPwdError] = useState("");
    const [dashPwdSaving, setDashPwdSaving] = useState(false);
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [branchAssignUser, setBranchAssignUser] = useState(null);
    const pathArray = [{ name: "User Management", url: null }];

    const [showBranchViewModal, setShowBranchViewModal] = useState(false);
    const [branchViewUser, setBranchViewUser] = useState(null);

    // ===== helpers =====
    const normalizeApi = (resp) => resp?.data?.data ?? resp?.data ?? resp;

    const getUserTypeLabel = (type) => {
        switch (Number(type)) {
            case 0: return "Web Admin";
            case 1: return "Delivery App";
            case 2: return "Picker App";
            case 3: return "Packer App";
            case 4: return "Picker + Packer";
            default: return "Unknown";
        }
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const data = await userApi.getUsers({
                limit: pageSize,
                page,
                status: selectedStatusFilter || null,
                userType: selectedUserType,
                roleId: selectedRoleFilter,
                search: searchText,
                sortModel,
            });

            const payload = normalizeApi(data);
            const list = payload?.result || payload?.data?.result || data?.data?.result || [];

            const formatted = list.map((user, index) => ({
                id: user.userId,
                sno: index + 1 + page * pageSize,
                name: user.name,
                username: user.userName,
                status: user.status,
                roleObjects: user.roles || [],
                roles: user.roles?.map((r) => r.name).join(", ") || "No Role",
                userType: user.userType,
                userTypeLabel: getUserTypeLabel(user.userType),
                assignedBranches: user.assignedBranches || [],
                showAssignedBranches: Boolean(user.showAssignedBranches),

                dashboardCardsEnabled: Number(user.dashboardCardsEnabled || 0),
                dashboardCardsHasPassword: Boolean(user.dashboardCardsHasPassword),
            }));

            setRows(formatted);
            setTotalUsers(payload?.totalRecords || data?.data?.totalRecords || 0);
        } catch (err) {
            console.error("Failed to fetch users:", err);
        } finally {
            setLoading(false);
        }
    }, [
        paginationModel,
        sortModel,
        refreshTable,
        searchText,
        selectedUserType,
        selectedRoleFilter,
        selectedStatusFilter,
    ]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        try {
            const access = JSON.parse(localStorage.getItem("accessData") || "{}");
            setIsSuperAdmin(Number(access.roleId || 0) === 1);
        } catch (e) {
            setIsSuperAdmin(false);
        }
    }, []);
    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [searchText, selectedUserType, selectedRoleFilter, selectedStatusFilter]);
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const sortModel2 = [{ field: "name", sort: "asc" }];
                const filterParams = {
                    limit: 1000,
                    page: 0,
                    status: 1,
                    sortModel: sortModel2,
                };

                const [branchData, employeeData] = await Promise.all([
                    branchApi.getAllBranches({
                        limit: 100,
                        page: 0,
                        status: 1,
                        sortModel: [{ field: "branchName", sort: "asc" }],
                    }),
                    employeeApi.getAllEmployees(filterParams),
                ]);

                setBranches(branchData?.data?.result || []);
                setEmployees(employeeData?.data?.result || []);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error fetching dropdown data:", error);
            }
        };

        fetchDropdownData();
    }, []);

    const handleStatusClick = (id, status) => {
        const nextStatus = status === 1 ? 2 : 1;
        const isDeactivating = nextStatus === 2;

        const confirmText = isDeactivating
            ? "Are you sure you want to deactivate this user?"
            : "Are you sure you want to activate this user?";
        const description = isDeactivating ? "This will make the user inactive." : "This will activate the user.";

        showConfirm(confirmText, { description, type: "info" }).then((confirmed) => {
            if (!confirmed) return;

            setSelectedUserId(id);
            setSelectedUserStatus(nextStatus);

            if (isDeactivating) setOpenInactivateModal(true);
            else updateUserStatus(id, nextStatus);
        });
    };

    const updateUserStatus = async (id, status, reason = null) => {
        setStatusUpdating(true);
        setIsSubmitting(true);
        try {
            const result = await userApi.updateStatus({ id, status, reason });

            if (result?.status === "SUCCESS") {
                showConfirm("Success", {
                    description: result.message || "User status updated.",
                    type: "success",
                    hideCancel: true,
                });
                setRefreshTable((prev) => !prev);
            } else {
                throw new Error(result?.message || "Update failed");
            }
        } catch (e) {
            showConfirm("Error", {
                description: e?.message || "Something went wrong.",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setOpenInactivateModal(false);
            setDeactivationReason("");
            setErrors({});
            setStatusUpdating(false);
            setIsSubmitting(false);
        }
    };

    const handleInactivateSubmit = () => {
        if (!deactivationReason.trim()) {
            setErrors({ reason: "Reason is required" });
            return;
        }
        updateUserStatus(selectedUserId, selectedUserStatus, deactivationReason);
    };

    const fetchUserLogs = async (id) => {
        try {
            const response = await userApi.getStatusLog({ id, page: 1, pageSize: 5 });
            if (response?.status === "SUCCESS") {
                setUserLogs(response.data.result);
                setStatusTypes(response.data.statusTypes);
                setShowLogModal(true);
            } else {
                throw new Error(response?.message || "Failed to fetch logs");
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error fetching user logs:", error);
        }
    };

    const handleAssignRoleClick = async (userId) => {
        setRoleAssigningUserId(userId);
        setShowRoleModal(true);

        try {
            const response = await menuApi.getRoles({ limit: 100, page: 0, status: 1 });
            const roleList = response?.data?.result || [];
            setRoles(roleList);

            const user = rows.find((r) => r.id === userId);
            if (user?.roles && user.roles !== "No Role") {
                const userRoleNames = user.roles.split(",").map((r) => r.trim());
                const matchedRole = roleList.find((r) => userRoleNames.includes(r.name));
                if (matchedRole) setSelectedRoleId(matchedRole.id);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch roles", error);
        }
    };

    const handleAssignRole = async () => {
        if (!selectedRoleId || !roleAssigningUserId) return;
        setRoleLoading(true);

        try {
            const result = await userApi.assignRole({
                userId: roleAssigningUserId,
                roleId: selectedRoleId,
            });

            if (result?.status === "SUCCESS") {
                showConfirm("Success", {
                    description: result.message || "Role assigned successfully.",
                    type: "success",
                    hideCancel: true,
                });
                setRefreshTable((prev) => !prev);
            } else {
                throw new Error(result?.message || "Role assignment failed");
            }
        } catch (error) {
            showConfirm("Error", {
                description: error?.message || "Something went wrong.",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setShowRoleModal(false);
            setSelectedRoleId(null);
            setRoleAssigningUserId(null);
            setRoleLoading(false);
        }
    };

    // ===== Revenue Cards password =====
    const openDashPwd = (u) => {
        setDashUser(u);
        setDashPwd1("");
        setDashPwd2("");
        setDashPwdError("");
        setShowDashPwdModal(true);
    };

    const saveDashPwd = async () => {
        if (!dashUser?.id) return;

        if (!dashPwd1.trim() || dashPwd1.length < 4) {
            setDashPwdError("Password must be at least 4 characters");
            return;
        }
        if (dashPwd1 !== dashPwd2) {
            setDashPwdError("Passwords do not match");
            return;
        }

        setDashPwdSaving(true);
        setDashPwdError("");

        try {
            const resp = await securityApi.setDashboardCardsPassword({
                userId: dashUser.id,
                enable: 1,
                password: dashPwd1,
            });

            // ✅ support BOTH return types: axios response OR direct json
            const d = resp;

            // console.log(d, "resp");
            const ok =
                d?.status === "SUCCESS" ||
                d?.status === true ||
                d?.data?.status === "SUCCESS";

            if (!ok) throw new Error(d?.message || "Failed");

            showConfirm("Success", {
                description: d?.message || "Revenue password saved.",
                type: "success",
                hideCancel: true,
            });

            // ✅ instantly update UI button text without waiting fetch
            setRows((prev) =>
                prev.map((r) =>
                    r.id === dashUser.id
                        ? { ...r, dashboardCardsEnabled: 1, dashboardCardsHasPassword: true }
                        : r
                )
            );

            setShowDashPwdModal(false);
            setDashPwd1("");
            setDashPwd2("");
            setDashPwdError("");
        } catch (e) {
            setDashPwdError(e?.message || "Failed");
        } finally {
            setDashPwdSaving(false);
        }
    };


    const disableDashCards = async (u) => {
        try {
            const resp = await securityApi.setDashboardCardsPassword({
                userId: u.id,
                enable: 0,
                password: null,
            });

            const d = resp?.data ? resp.data : resp;
            const ok = d?.status === "SUCCESS" || d?.status === true;

            if (!ok) throw new Error(d?.message || "Failed");

            showConfirm("Success", {
                description: d?.message || "Revenue disabled.",
                type: "success",
                hideCancel: true,
            });

            // ✅ update UI instantly
            setRows((prev) =>
                prev.map((r) =>
                    r.id === u.id
                        ? { ...r, dashboardCardsEnabled: 0 } // keep hasPassword true if you want
                        : r
                )
            );
        } catch (e) {
            showConfirm("Error", {
                description: e?.message || "Failed to disable",
                type: "warning",
                hideCancel: true,
            });
        }
    };


    // ===== UI styles for compact actions =====
    const btnSx = {
        padding: "6px 10px",
        borderRadius: 10,
        minWidth: 120,
        fontWeight: 600,
        whiteSpace: "nowrap",
    };

    const actionWrapStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
        paddingTop: 4,
        paddingBottom: 4,
    };

    const actionGridStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(120px, 1fr))",
        gap: 8,
        alignItems: "center",
        padding: "6px 0",
    };

    const actionBtnStyle = {
        width: "100%",
        height: 34,
        padding: "0 10px",
        fontSize: "0.82rem",
        fontWeight: 600,
        borderRadius: 10,
        whiteSpace: "nowrap",
    };

    const revenueBadgeStyle = {
        gridColumn: "1 / -1",
        fontSize: "0.75rem",
        padding: "4px 10px",
        borderRadius: 999,
        width: "fit-content",
        marginTop: 2,
    };

    const filterCardStyle = {
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 12,
        padding: "14px",
        marginBottom: 14,
    };

    const chipWrapStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        paddingTop: 6,
    };

    const chipStyle = {
        background: "#eef4ff",
        color: "#1d4ed8",
        border: "1px solid #cfe0ff",
        borderRadius: 999,
        padding: "4px 10px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.2,
    };



    const miniLinkBtnStyle = {
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
    };

    const roleCellWrapStyle = {
        paddingTop: 8,
        paddingBottom: 8,
        display: "flex",
        flexDirection: "column",
        gap: 6,
    };

    const roleTitleStyle = {
        fontWeight: 700,
        fontSize: 13,
        color: "#212529",
    };

    const roleSubtleTextStyle = {
        color: "#6c757d",
        fontSize: 12,
        fontWeight: 500,
    };

    const branchPreviewWrapStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        alignItems: "center",
    };
    const statusBtnStyle = (active) => ({
        width: 120,
        height: 34,
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 13,
        padding: "0 14px",
        border: "1px solid rgba(0,0,0,0.08)",
        backgroundColor: active ? "#198754" : "#6c757d",  // green / grey
        color: "#fff",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    });
    const columns = [
        { field: "sno", headerName: "S No", width: 80 },
        { field: "name", headerName: "Name", width: 160 },
        { field: "username", headerName: "Username", width: 160 },
        { field: "userTypeLabel", headerName: "User Type", width: 130 },
        {
            field: "roles",
            headerName: "Roles",
            minWidth: 300,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const user = rows.find((r) => r.id === params.id);
                const hasRoles = user?.roles && user.roles !== "No Role";
                const isDeliveryRole = Boolean(user?.showAssignedBranches);
                const assignedBranches = user?.assignedBranches || [];
                const previewBranches = assignedBranches.slice(0, 2);
                const remainingCount = assignedBranches.length - previewBranches.length;

                return (
                    <div style={roleCellWrapStyle}>
                        <div style={roleTitleStyle}>{params.value}</div>

                        <Button
                            size="sm"
                            variant="secondary"
                            style={actionBtnStyle}
                            onClick={() => handleAssignRoleClick(params.id)}
                        >
                            {hasRoles ? "Edit Role" : "Assign Role"}
                        </Button>

                        {isDeliveryRole && (
                            <>
                                {assignedBranches.length > 0 ? (
                                    <div style={branchPreviewWrapStyle}>
                                        {previewBranches.map((branch) => (
                                            <span key={branch.branchId} style={chipStyle}>
                                                {branch.branchName}
                                            </span>
                                        ))}

                                        {remainingCount > 0 && (
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                style={miniLinkBtnStyle}
                                                onClick={() => {
                                                    setBranchViewUser(user);
                                                    setShowBranchViewModal(true);
                                                }}
                                            >
                                                View All (+{remainingCount})
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <div style={roleSubtleTextStyle}>No Branch Assigned</div>
                                )}
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            width: 170,
            sortable: false,
            renderCell: (params) => {
                const active = Number(params.value) === 1;

                return (
                    <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                        <Button
                            size="sm"
                            className="mt-4"
                            style={statusBtnStyle(active)}
                            onClick={() => handleStatusClick(params.id, params.value)}
                        >
                            {active ? "Active" : "Inactive"}
                        </Button>
                    </div>
                );
            },
        },

        {
            headerName: "Actions",
            flex: 1,
            minWidth: 280,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const user = rows.find((r) => r.id === params.id);

                const revenueEnabled = Number(user?.dashboardCardsEnabled || 0) === 1;
                const isAppUser = [1, 2, 3, 4].includes(Number(user?.userType));
                return (
                    <div style={actionGridStyle}>
                        <Button
                            size="sm"
                            style={actionBtnStyle}
                            onClick={() => {
                                setEditingUserId(params.id);
                                setShowCreateModal(true);
                            }}
                        >
                            Edit
                        </Button>

                        <Button
                            size="sm"
                            variant="info"
                            style={actionBtnStyle}
                            onClick={() => fetchUserLogs(params.id)}
                        >
                            View Log
                        </Button>


                        <Button
                            size="sm"
                            variant="warning"
                            style={actionBtnStyle}
                            onClick={() => {
                                setPasswordUserId(params.id);
                                setShowPasswordModal(true);
                            }}
                        >
                            Change Password
                        </Button>

                        {/* ✅ Revenue password button (Super Admin only) */}
                        {isSuperAdmin && (
                            <>
                                <Button
                                    size="sm"
                                    variant={user?.dashboardCardsHasPassword ? "outline-warning" : "outline-primary"}
                                    style={actionBtnStyle}   // ✅ small (NOT full row)
                                    onClick={() => openDashPwd({ ...user, id: params.id })}
                                >
                                    {user?.dashboardCardsHasPassword ? "Revenue Pwd" : "Set Revenue Pwd"}
                                </Button>

                                <Button
                                    size="sm"
                                    variant={revenueEnabled ? "outline-danger" : "outline-success"}
                                    style={actionBtnStyle}   // ✅ small (NOT full row)
                                    onClick={() => {
                                        if (revenueEnabled) disableDashCards({ ...user, id: params.id });
                                        else openDashPwd({ ...user, id: params.id }); // enabling requires password
                                    }}
                                >
                                    {revenueEnabled ? "Disable Revenue" : "Enable Revenue"}
                                </Button>
                            </>
                        )}

                        {isAppUser && (
                            <Button
                                size="sm"
                                variant="outline-dark"
                                style={actionBtnStyle}
                                onClick={() => {
                                    setBranchAssignUser(user);
                                    setShowBranchModal(true);
                                }}
                            >
                                Assign Branches
                            </Button>
                        )}

                    </div>
                );
            },
        }

    ];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Users</h5>
                <div className="d-flex gap-2">
                    <Button
                        onClick={() => {
                            setEditingUserId(null);
                            setShowCreateModal(true);
                        }}
                    >
                        Create User
                    </Button>
                </div>
            </div>

            {(loading || statusUpdating) && <Loader />}
            <div style={filterCardStyle}>
                <Row className="g-3 align-items-end">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Search</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search name / username"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>User Type</Form.Label>
                            <Form.Select
                                value={selectedUserType}
                                onChange={(e) => setSelectedUserType(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="0">Web Admin</option>
                                <option value="1">Delivery App</option>
                                <option value="2">Picker App</option>
                                <option value="3">Packer App</option>
                                <option value="4">Picker + Packer</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={selectedRoleFilter}
                                onChange={(e) => setSelectedRoleFilter(e.target.value)}
                            >
                                <option value="">All Roles</option>
                                {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={selectedStatusFilter}
                                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="2">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={1}>
                        <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={() => {
                                setSearchText("");
                                setSelectedUserType("");
                                setSelectedRoleFilter("");
                                setSelectedStatusFilter("");
                            }}
                        >
                            Reset
                        </Button>
                    </Col>
                </Row>
            </div>
            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    paginationMode="server"
                    sortingMode="server"
                    getRowHeight={() => "auto"}
                    rowCount={totalUsers}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    loading={loading}
                    disableColumnFilter
                    disableDensitySelector
                    disableRowSelectionOnClick
                    sx={{
                        [`& .${gridClasses.cell}`]: {
                            py: 1.5,
                            alignItems: "flex-start",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            fontSize: 13,
                            fontWeight: 700,
                            backgroundColor: "#f8f9fa",
                        },
                        "& .MuiDataGrid-row": {
                            borderBottom: "1px solid #f1f3f5",
                        },
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                            outline: "none",
                        },
                    }}
                />
            </Box>

            {/* Create User Modal */}
            {showCreateModal && (
                <UserCreateForm
                    show={showCreateModal}
                    handleClose={() => {
                        setShowCreateModal(false);
                        setEditingUserId(null);
                    }}
                    handleRefresh={() => setRefreshTable((prev) => !prev)}
                    editingUserId={editingUserId}
                    branches={branches}
                    employees={employees}
                />
            )}

            {/* Deactivation Modal */}
            <Modal show={openInactivateModal} onHide={() => setOpenInactivateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Enter Deactivation Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={deactivationReason}
                            onChange={(e) => setDeactivationReason(e.target.value)}
                            isInvalid={!!errors.reason}
                        />
                        <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleInactivateSubmit} disabled={isSubmitting}>
                        {isSubmitting ? "Updating..." : "Submit"}
                    </Button>
                    <Button variant="secondary" onClick={() => setOpenInactivateModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Role Assignment Modal */}
            <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Assign Role</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Select Role</Form.Label>
                        <Form.Select value={selectedRoleId || ""} onChange={(e) => setSelectedRoleId(e.target.value)}>
                            <option value="">-- Select a Role --</option>
                            {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleAssignRole} disabled={roleLoading}>
                        {roleLoading ? "Assigning..." : "Assign"}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Logs Modal */}
            <LogModal show={showLogModal} onHide={() => setShowLogModal(false)} logs={userLogs} statusTypes={statusTypes} />

            {/* Change Password Modal */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            isInvalid={!!passwordError}
                            placeholder="Enter new password"
                        />
                        <Form.Control.Feedback type="invalid">{passwordError}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={async () => {
                            if (!newPassword.trim()) {
                                setPasswordError("Password is required");
                                return;
                            }

                            setPasswordChanging(true);
                            try {
                                const result = await userApi.changePassword({
                                    userId: passwordUserId,
                                    newPassword,
                                });

                                if (result?.status === "SUCCESS") {
                                    showConfirm("Success", {
                                        description: "Password changed successfully.",
                                        type: "success",
                                        hideCancel: true,
                                    });
                                    setShowPasswordModal(false);
                                    setNewPassword("");
                                    setPasswordError("");
                                } else {
                                    throw new Error(result?.message || "Failed to change password.");
                                }
                            } catch (err) {
                                showConfirm("Error", {
                                    description: err?.message,
                                    type: "warning",
                                    hideCancel: true,
                                });
                            } finally {
                                setPasswordChanging(false);
                            }
                        }}
                        disabled={passwordChanging}
                    >
                        {passwordChanging ? "Updating..." : "Submit"}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Revenue Password Modal */}
            <Modal show={showDashPwdModal} onHide={() => !dashPwdSaving && setShowDashPwdModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{dashUser?.dashboardCardsHasPassword ? "Change Revenue Password" : "Set Revenue Password"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-2">
                        <b>User:</b> {dashUser?.name || dashUser?.username || dashUser?.id}
                    </div>

                    <Form.Group className="mb-2">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control type="password" value={dashPwd1} onChange={(e) => setDashPwd1(e.target.value)} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control type="password" value={dashPwd2} onChange={(e) => setDashPwd2(e.target.value)} />
                    </Form.Group>

                    {dashPwdError && <div className="text-danger">{dashPwdError}</div>}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" disabled={dashPwdSaving} onClick={() => setShowDashPwdModal(false)}>
                        Cancel
                    </Button>

                    <Button variant="primary" disabled={dashPwdSaving} onClick={saveDashPwd}>
                        {dashPwdSaving ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {showBranchModal && (
                <AssignDeliveryBranchesModal
                    show={showBranchModal}
                    onHide={() => {
                        setShowBranchModal(false);
                        setBranchAssignUser(null);
                    }}
                    user={branchAssignUser}
                    branches={branches}
                    onSaved={() => setRefreshTable((prev) => !prev)}
                />
            )}

            <Modal
                show={showBranchViewModal}
                onHide={() => {
                    setShowBranchViewModal(false);
                    setBranchViewUser(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Assigned Branches</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <div><b>User:</b> {branchViewUser?.name}</div>
                        <div><b>Username:</b> {branchViewUser?.username}</div>
                    </div>

                    {branchViewUser?.assignedBranches?.length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {branchViewUser.assignedBranches.map((branch) => (
                                <span key={branch.branchId} style={chipStyle}>
                                    {branch.branchName}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: "#6c757d" }}>No Branch Assigned</div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowBranchViewModal(false);
                            setBranchViewUser(null);
                        }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};

export default AllUsers;
